// src/hooks/useMetaPixel.js
//
// Dispara PageView do Meta Pixel só em rotas públicas + só quando o
// usuário NÃO está autenticado.
//
// Motivação (fev/26 — LGPD + eficiência de mídia):
//   1. Paths do app autenticado (ex.: /conversations/<uuid>,
//      /contatos/<uuid>) vazam IDs de clientes finais pra Meta se o
//      pixel for disparado ali — problema de LGPD claro.
//   2. Re-targeting de cliente pago é dinheiro jogado fora — se o user
//      já entrou no dashboard, ele já converteu; não precisa ver anúncio.
//   3. Volume de PageView cheio (todas navegações internas) infla
//      métricas e engana os algoritmos de otimização da Meta.
//
// Base do pixel é carregada em public/index.html (fbq inicializado com
// PIXEL ID). Aqui só decidimos quando disparar o `track('PageView')`.
//
// Estratégia:
//   - Se `localStorage.token` existe → usuário logado → NÃO dispara.
//   - Se não existe → usuário anônimo (landing/login/register/planos/
//     checkout) → dispara PageView em cada mudança de rota.
//
// Eventos de conversão saem em pontos-chave via trackFbqEvent (abaixo):
// InitiateCheckout no clique do pagamento e, na volta do Stripe com o
// cartão aceito, CompleteRegistration + StartTrial (com teste) ou Purchase
// (sem teste) — ver "Pixel na ativação" no Register.js. Na landing, um
// LP_Clique por botão (pages/landing/comum.js).
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export default function useMetaPixel() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Usuário autenticado → não trackear (LGPD + evita re-target).
    if (localStorage.getItem('token')) return;
    guardarCliqueDoAnuncio(location.search);
    if (typeof window.fbq !== 'function') return;

    window.fbq('track', 'PageView');
  }, [location.pathname, location.search]);
}

// ─── Atribuição pra API de Conversões (out/26) ───────────────────────────
// O servidor manda à Meta o início do teste e a cobrança do 8º dia; pra a
// Meta ligar esses eventos ao clique no anúncio, o checkout leva os cookies
// do Pixel (`_fbp`, e `_fbc` de quem veio de anúncio). Com o Pixel bloqueado
// não há cookie: o `fbclid` que o anúncio põe na URL é guardado aqui e vira
// o `fbc` no formato da Meta (fb.1.<ms>.<fbclid>). Antes do bloqueio do
// fbq, de propósito — é justamente o caso sem Pixel.
const CHAVE_FBC = 'cetto_fbc';

function guardarCliqueDoAnuncio(search) {
  try {
    const fbclid = new URLSearchParams(search).get('fbclid');
    if (fbclid && /^[A-Za-z0-9_-]{1,400}$/.test(fbclid)) {
      localStorage.setItem(CHAVE_FBC, `fb.1.${Date.now()}.${fbclid}`);
    }
  } catch {
    // Sem localStorage: segue sem.
  }
}

function lerCookie(nome) {
  const achado = document.cookie.split('; ').find((c) => c.startsWith(`${nome}=`));
  return achado ? decodeURIComponent(achado.slice(nome.length + 1)) : null;
}

// Vai no corpo do POST /billing/create-checkout-session.
export function dadosDeAtribuicao() {
  try {
    let fbc = lerCookie('_fbc');
    if (!fbc) {
      try { fbc = localStorage.getItem(CHAVE_FBC); } catch { fbc = null; }
    }
    return { fbp: lerCookie('_fbp'), fbc: fbc || null };
  } catch {
    return { fbp: null, fbc: null };
  }
}

// ─── Conversion events ────────────────────────────────────────────────────
// Ao contrário do PageView (gated por auth), estes eventos SEMPRE disparam
// — são pontos de conversão que a Meta Ads precisa ver mesmo em user
// autenticado (o InitiateCheckout e a ativação na volta do Stripe
// acontecem com o token da etapa 1 já no localStorage).
//
// Uso:
//   trackFbqEvent('InitiateCheckout',     { value: 139, currency: 'BRL', content_name: 'STARTER' });
//   trackFbqEvent('CompleteRegistration', { value: 139, currency: 'BRL', content_name: 'STARTER' }, { eventID: 'cs_…' });
//
// `options` (opcional) vai como 4º argumento do fbq — o `eventID` é o que a
// Meta usa pra não contar duas vezes o mesmo evento vindo do navegador e
// do servidor (API de Conversões).
export function trackFbqEvent(event, params, options) {
  if (typeof window === 'undefined') return;
  if (typeof window.fbq !== 'function') return;
  try {
    if (options) {
      window.fbq('track', event, params || {}, options);
    } else if (params && Object.keys(params).length > 0) {
      window.fbq('track', event, params);
    } else {
      window.fbq('track', event);
    }
  } catch (e) {
    // Falha no fbq nunca pode derrubar o fluxo de checkout. Só loga.
    // eslint-disable-next-line no-console
    console.warn('[fbq] track falhou:', event, e);
  }
}

// Mapping plan → valor em BRL enviado à Meta Ads. Precisa bater exatamente
// com o que o Stripe cobra — valor divergente aqui envenena o bidding por
// valor. Alinhado com getPlans() em Register.js e os planos da landing
// (pages/landing/Planos.js).
// PLUS (out/26): plano sob medida, R$ 249 — também precisa bater com o Stripe.
const PLAN_VALUE_BRL = { STARTER: 139, PLUS: 249, BUSINESS: 339 };

export function getPlanValueBRL(planType) {
  const plan = (planType || 'STARTER').toUpperCase();
  return PLAN_VALUE_BRL[plan] || 139;
}
